import {
  Delete,
  ExpandLess,
  ExpandMore,
  MoveToInbox
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Collapse,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useTheme
} from "@mui/material";
import { grey, purple } from "@mui/material/colors";
import { useSnackbar } from "notistack";
import React from "react";
import Viewer from "react-viewer";
import { useAppDispatch } from "../../redux";
import { useLazyGetAttachmentUrlQuery } from "../../redux/services";
import { closeBackdrop, openBackdrop } from "../../redux/slices/backdrop";
import { Attachment } from "../../redux/slices/material";
import { UploadConfig } from "../documents";
import { MyPaper } from "../myPaper";
import { fileUploadWarningMessage, pattern } from "../uploadImage";
import "./index.css";

type PrimaryConfig = {
  primaryImageId: string;
  onChangePrimary: (id: string) => void;
}

type ImageUploadListProps = {
  title: string;
  hasReleased?: boolean;
  maxFiles?: number;
  sizeLimit?: number | string;
  files?: (Attachment | File)[];
  onDelete: (file: Attachment | File, index: number) => void;
  onUpload: (file: File) => void;
  multiple?: {
    onMultiUpload: (files: File[]) => void;
  };
  disabled?: boolean;
  uploadError?: boolean;
  uploadAllFileTypes?: boolean;
  disableCollapse?: boolean;
  stack?: boolean;
  hidePreview?: boolean;
  loading?: boolean;
  primaryConfig?: PrimaryConfig;
  readOnly?: boolean;
  accept?: string;
  shadow?: boolean;
  uploadContentDetail?: UploadConfig;
};

const ImageUploadList: React.FC<ImageUploadListProps> = ({
  maxFiles = 1,
  title,
  files = [],
  sizeLimit = 1,
  onDelete,
  onUpload,
  disabled = false,
  uploadError = false,
  uploadAllFileTypes = false,
  uploadContentDetail,
  disableCollapse = false,
  stack = true,
  hidePreview = false,
  // loading = false,
  primaryConfig,
  readOnly = false,
  accept = '',
  shadow = true,
  hasReleased = false,
  multiple
}) => {

  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [getAttachementById] = useLazyGetAttachmentUrlQuery()

  const inputRef = React.useRef<HTMLInputElement>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);

  const [open, updateOpen] = React.useState(true);
  const [showAlert, setShowAlert] = React.useState(false);
  const [previewImg, setPreviewImg] = React.useState<{ src: string, alt: string }>();
  const [key, setKey] = React.useState(crypto.randomUUID());

  // Function to prevent default behavior for drag over
  const handleDragOver = (event: any) => {
    if (containerRef?.current) {
      containerRef.current.style.border = `2px dotted ${theme.palette.primary.main}`;
      containerRef.current.style.backgroundColor = `${purple[50]}`;
      containerRef.current.style.opacity = "0.8";
    }
    event?.preventDefault();
  };

  const makeBorderNone = () => {
    if (containerRef?.current) {
      containerRef.current.style.border = `1px dashed ${grey[500]}`;
      containerRef.current.style.backgroundColor = `${grey[100]}`;
      containerRef.current.style.opacity = "1";
    }
  };

  const fileLimitCheck = (file: File) => {
    const sizeInBytes = file?.size ?? 0;
    const fileName = file?.name ?? "";
    const sizeInKB = sizeInBytes / 1024;
    let sizeInMB: number = sizeInKB / 1024;
    let stringified = sizeLimit.toString();
    let isGB = stringified.includes("GB");
    let limit: number = isGB ? parseInt(stringified.split("GB")[0] ?? 0) : sizeLimit as number;
    if (isGB) {
      sizeInMB = sizeInMB / 1024;
    }
    if (sizeInMB > limit) {
      enqueueSnackbar(
        `Image Size should be less than or equal to ${sizeLimit}${isGB ? "" : "MB"
        }`,
        { variant: "warning" }
      );
      return false;
    }
    if (!pattern.test(fileName ?? "")) {
      enqueueSnackbar(fileUploadWarningMessage, { variant: "warning" });
      return false;
    }
    return true;
  };

  // Function to handle image drop
  const handleImageUpload = (event: any) => {
    setKey(crypto.randomUUID());
    makeBorderNone();
    event?.preventDefault();
    const file = event?.dataTransfer?.files?.[0] || inputRef?.current?.files?.[0];
    if (uploadContentDetail?.accept && !uploadContentDetail?.accept?.split(",").includes(file.type)) {
      enqueueSnackbar(`Please upload only ${uploadContentDetail?.fileTypes}`, { variant: "warning" });
      return;
    }

    if (disabled) {
      return false;
    }
    if (maxFiles === files.length) {
      setShowAlert(true);
      return;
    } else {
      setShowAlert(false);
    }
    if (fileLimitCheck(file)) {
      onUpload(file);
    }
  };

  const handleMultiFileUpload = (event: any) => {
    setKey(crypto.randomUUID());
    makeBorderNone();
    event?.preventDefault();
    let files =
      event?.dataTransfer?.files || inputRef?.current?.files;
    if (uploadContentDetail?.accept) {
      const acceptedFiles = uploadContentDetail?.accept?.split(",");
      files = [...files].filter((f: File) => acceptedFiles.includes(f.type) && fileLimitCheck(f));
      console.log("acceptedFiles", acceptedFiles, files)
    }

    if (disabled) {
      return false;
    }
    if (maxFiles === files.length) {
      setShowAlert(true);
      return;
    } else {
      setShowAlert(false);
    }

    multiple?.onMultiUpload(files);
  };

  const openFileInput = () => {
    if (disabled) {
      return false;
    }
    if (maxFiles === files.length) {
      setShowAlert(true);
      return false;
    } else {
      setShowAlert(false);
    }
    inputRef?.current?.click();
  };

  const openPreview = async (index: number) => {
    let file: any = files?.[index];
    if (file?.url || file?.name) {

      let uri = decodeURIComponent((file as Attachment)?.url ?? "") ?? URL.createObjectURL(file as File);

      if ((file as Attachment)?.url) {
        if (file.object_type && file.object_id) {
          try {
            dispatch(openBackdrop('Loading...'));
            const res = await getAttachementById({ id: `${file._id}`, object_type: file.object_type, object_id: file.object_id });
            uri = res.data;
          } catch (error) {
            console.log('error:', error)
          } finally {
            dispatch(closeBackdrop());
          }
        }
      } else {
        uri = URL.createObjectURL(file as File);
      }

      setPreviewImg({ src: uri, alt: (file as File)?.name || (file as Attachment)?.file_name })
    }
  };

  const onDeleteHandler = (file: File | Attachment, idx: number) => {
    onDelete(file, idx);
    setShowAlert(false);
  };

  return (
    (<MyPaper padding={0} shadow={shadow ? false : "unset"}>
      <>

        <Viewer
          key={previewImg?.src ?? ""}
          // downloadInNewWindow={true}
          // downloadable={true}
          visible={previewImg ? true : false}
          onClose={() => { setPreviewImg(undefined); }}
          images={previewImg ? [previewImg] : [{ src: "", alt: "" }]}
          changeable={false}
          zIndex={1400}
        />

        {!disableCollapse && (
          <Stack
            direction={"row"}
            sx={{
              justifyContent: "space-between",
              alignItems: "center",
              px: 2,
              borderBottom: (theme) => `1px solid ${theme.palette.divider}`
            }}>
            <Typography variant="body1" sx={{
              fontFamily: "htrts_medium"
            }}>
              {title}
            </Typography>

            <Stack direction="row" spacing={2}>
              <IconButton onClick={() => updateOpen(!open)}>
                {!open && <ExpandMore />}
                {open && <ExpandLess />}
              </IconButton>
            </Stack>
          </Stack>
        )}

        <Collapse in={open} timeout="auto" unmountOnExit>
          <Stack
            sx={{
              flexDirection: stack ? "column" : "row",
              alignItems: "center",
              p: 2,
              gap: files.length ? 2 : 0
            }}>
            {!readOnly && <Stack
              ref={containerRef}
              onMouseLeave={() => makeBorderNone()}
              onDragExit={() => makeBorderNone()}
              onDragLeave={() => makeBorderNone()}
              sx={{
                py: 1,
                px: 2,
                width: "auto",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 4,
                bgcolor: grey[100],
                height: "max-content",
                cursor:
                  maxFiles === files.length || disabled ? "no-drop" : "pointer",
                border: `1px dashed ${grey[500]}`,
              }}
              spacing={1}
              component={"div"}
              onDragOver={handleDragOver}
              onDrop={multiple?.onMultiUpload ? (event) => handleMultiFileUpload(event) : (event) => handleImageUpload(event)}
              onClick={openFileInput}
            >
              <MoveToInbox color="primary" sx={{ fontSize: 80 }} />
              <Typography variant={"body1"}>
                Click or drag {uploadAllFileTypes ? "files" : uploadContentDetail?.placeholderText || "image"} to this
                area to upload
              </Typography>
              <Stack sx={{ alignItems: "center" }}>
                <Typography variant={"caption"}>
                  Upload {uploadAllFileTypes ? "files" : uploadContentDetail?.placeholderText || "image"} less than or
                  equal to{" "}
                  <strong style={{ fontSize: "14px" }}>
                    {sizeLimit}
                    {sizeLimit.toString().includes("GB") ? "" : "MB"}
                  </strong>
                </Typography>
                {!uploadAllFileTypes && (
                  <Typography variant={"caption"} gutterBottom>
                    Please upload only these formats:{" "}
                    <strong>{uploadContentDetail?.fileTypes || "JPEG, JPG, PNG"}</strong>.
                  </Typography>
                )}
                {showAlert && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    The upload is limited to {maxFiles}{" "}
                    {uploadAllFileTypes ? "files" : uploadContentDetail?.placeholderText || "image"}
                  </Alert>
                )}
                {uploadError && (
                  <Typography variant="caption" color="error">
                    {`Please upload the ${uploadAllFileTypes ? "files" : uploadContentDetail?.placeholderText || "image"
                      }.`}
                  </Typography>
                )}
              </Stack>
              <input
                key={key}
                id="file-upload"
                hidden
                type="file"
                ref={inputRef}
                multiple={multiple?.onMultiUpload ? true : false}
                accept={
                  uploadAllFileTypes ? "" : uploadContentDetail?.accept || accept || "image/jpeg, image/png, image/jpg"
                }
                onChange={multiple?.onMultiUpload ? (event) => handleMultiFileUpload(event) : (event) => handleImageUpload(event)}
              />
            </Stack>}

            {hidePreview && !multiple &&
              ((files?.[0] as File)?.name ||
                (files?.[0] as Attachment)?.file_name) && (
                <Button
                  disabled={disabled}
                  onClick={() => onDeleteHandler(files?.[0], 0)}
                  variant={"outlined"}
                  size={"small"}
                >
                  Clear
                </Button>
              )}

            <Stack
              sx={{
                flexDirection: "row",
                gap: 2,
                flexWrap: "wrap",
                alignItems: "center",
                display: hidePreview ? "none" : "flex"
              }}>
              {files?.map((file, idx: number) => {

                let src = "/imageNotFound.jpg";
                let name = "";

                if ((file as Attachment)?.url) {
                  src = (file as Attachment)?.url || "/imageNotFound.jpg";
                  name = (file as Attachment)?.file_name;
                } else if ((file as File)?.name) {
                  src = URL.createObjectURL(file as File);
                  name = (file as File)?.name;
                }

                const isPrimary = primaryConfig?.primaryImageId === (file as Attachment)?._id || (primaryConfig?.primaryImageId === name && !(file as Attachment)?.file_name);

                return (
                  (<Stack
                    sx={{
                      justifyContent: "center",
                      alignItems: 'center'
                    }}>
                    <Stack className={`image-container ${isPrimary ? "selected" : ""}`} sx={{ border: `5px solid ${grey[200]}`, boxShadow: 2 }}>

                      {["jpeg", "jpg", "png"].some((s) => name?.includes(s))
                        ? (<img
                          className="image-element"
                          src={src}
                          height={"100%"}
                          width={"100%"}
                          alt={`${name ?? "alt"}`}
                          loading="lazy"
                        />)
                        : (<div role="img" aria-label={name}>{name}</div>)}

                      {!readOnly && !hasReleased && <Delete color="error" fontSize="small" className={"delete"} onClick={() => onDeleteHandler(file, idx)} />}
                      <Button variant="contained" size="small" className={"preview"} onClick={() => openPreview(idx)} >Preview</Button>
                      <Box className={"preview-wrapper"} sx={{ display: "none", position: "absolute", top: 0, left: 0, right: 0, bottom: 0, background: "black", opacity: 0.25, zIndex: 1 }} ></Box>
                    </Stack>
                    {primaryConfig && !readOnly &&
                      <Tooltip title="Make this Image as Primary image">
                        <Checkbox title="Make Primary" checked={isPrimary} onChange={() => primaryConfig?.onChangePrimary((file as Attachment)?._id || name)} color={"success"} />
                      </Tooltip>
                    }
                  </Stack>)
                );
              })}
            </Stack>
          </Stack>
        </Collapse>
      </>
    </MyPaper>)
  );
};

export { ImageUploadList };

